import { makeAutoObservable } from 'mobx';
import { ExpertConfig } from '@ai/common/src/types/expert';
import { LpcmPlayer } from '../utils/lpcmPlayer';
import { ttsTextPreprocessor } from '@ai/common/src/utils/ttsTextPreprocessor';
import { widgetService } from '../api/services/widget.service';
import { ExpertDefaults } from '@ai/common/src/types/expert';

class SpeechSynthesizerStore {
  // isLoading: boolean = false;
  error: string = '';
  ws: WebSocket | null = null;
  audioPlayer?: LpcmPlayer;

  config: ExpertConfig | null;
  constants: ExpertDefaults;

  onError?: (v: string) => void;
  onAudioStartPlay?: () => void;
  onAudioStopPlay?: () => void;

  constructor(config: ExpertConfig | null, constants: ExpertDefaults) {
    this.config = config;
    this.constants = constants;

    makeAutoObservable(this, {}, { autoBind: true });
  }

  // setLoading(value: boolean) {
  //   this.isLoading = value;
  // }

  setWs(ws: WebSocket | null) {
    this.ws = ws;
  }

  setIsPhraseFinished = () => {
    if (!this.ws) return;

    this.ws.send(JSON.stringify({ status: 'completed' }));
  };

  async say(expertId: string, dialogId: string, text: string, sampleRate: number) {
    return new Promise((resolve) => {
      if (!this.ws) return;

      const processedText = ttsTextPreprocessor(
        text,
        this.config?.case_sensitive_replacements || [],
        this.config?.case_insensitive_replacements || [],
        this.config?.voice || this.constants.voice,
      );

      if (!processedText) {
        resolve(true);
        return;
      }

      this.ws.send(
        JSON.stringify({
          expert_id: expertId,
          dialog_id: dialogId,
          text: processedText,
          sample_rate: sampleRate,
        }),
      );

      resolve(true);
    });
  }

  clear() {
    console.log('TTS WS cleared');
    this.audioPlayer?.clear();
    this.audioPlayer = undefined;

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  async init(sampleRate: number) {
    return new Promise((resolve, reject) => {
      // this.setLoading(true);

      const ws = new WebSocket(`${widgetService.rootOrigin}/widget/api/v1/tts-stream-ws`);
      this.setWs(ws);

      this.audioPlayer = new LpcmPlayer({
        numChannels: 1,
        sampleRate: sampleRate,
        onPlaybackEnded: this.onAudioStopPlay,
        onStartPlaying: this.onAudioStartPlay,
      });

      ws.onopen = () => resolve(true);

      ws.onmessage = async (event: MessageEvent) => {
        if (event.data instanceof Blob) {
          // Это байтовый чанк аудио
          this.audioPlayer?.setIsAllChunksRecieved(false);
          const chunk = await new Response(event.data).arrayBuffer();
          this.audioPlayer?.enqueue(chunk);
        } else {
          // Это JSON-сообщение
          const message = JSON.parse(event.data);
          if (message.status === 'completed') {
            this.audioPlayer?.setIsAllChunksRecieved(true);
          }

          if (message.error) {
            this.onError?.(message.error);
            console.error(message.error);
            // this.setLoading(false);
            reject(message.error);
          }
        }
      };

      ws.onerror = (error) => {
        this.onError?.('Ошибка при синтезе речи');
        console.error(error);
        reject(error);
        // this.setLoading(false);
      };

      ws.onclose = () => {
        console.log('TTS: websocket connection closed');
        // this.setLoading(false);
      };
    });
  }

  stop() {
    console.log('##### STOP');

    this.audioPlayer?.stop();

    this.clear();
    this.audioPlayer = undefined;
    // Закрываем вебсокет, если он открыт
    if (this.ws) {
      this.ws.close();
      this.setWs(null); // Очищаем ссылку на вебсокет
    }

    console.log('Speech synthesis stopped');
  }
}

export default SpeechSynthesizerStore;
